import { assign, createMachine } from "xstate";
import { fetchGroups, IGroup } from "@nju/data-access-portal-dashboard";
import {
  createOrchestrator,
  fetchOrchestrators,
  IFtthOrchestrator,
  IMobileOrchestrator,
  IModemReturnOrchestrator,
  IMultiOrderOrchestrator,
} from "@nju/data-access-portal-orchestrator";
import {
  fetchOrderProducts,
  IFtthOrderProductsResponse,
  IMobileOrderProductsResponse,
} from "@nju/data-access-portal-product";
import { verifyAddress } from "@nju/data-access-portal-wwt";
import {
  fetchClientAddress,
  IClientAddress,
} from "@nju/data-access-sales-client";

interface IContext {
  groups?: IGroup[];
  ftthOrchestrator?: IFtthOrchestrator;
  mobileOrchestrator?: IMobileOrchestrator;
  multiOrderOrchestrator?: IMultiOrderOrchestrator;
  modemReturn?: IModemReturnOrchestrator;
  ftthProducts?: IFtthOrderProductsResponse;
  mobileProducts?: IMobileOrderProductsResponse;
  address?: IClientAddress;
  isAddressInFtthRange?: boolean;
}

type IEvent =
  | { type: "ERROR" }
  | {
      type: "SET_DATA";
      groups?: IGroup[];
      ftthOrchestrator?: IFtthOrchestrator;
      mobileOrchestrator?: IMobileOrchestrator;
      multiOrderOrchestrator?: IMultiOrderOrchestrator;
      modemReturn?: IModemReturnOrchestrator;
      ftthProducts?: IFtthOrderProductsResponse;
      mobileProducts?: IMobileOrderProductsResponse;
    }
  | { type: "NEW_MULTI_ORDER" }
  | { type: "NEW_FTTH_ORDER" }
  | { type: "NEW_MULTI_ORDER_SUCCESS" }
  | { type: "NEW_FTTH_ORDER_SUCCESS" }
  | { type: "PAYMENT_ERROR" }
  | { type: "UPDATE_ADDRESS" }
  | { type: "ANOTHER_AGENT_IN_PROGRESS" }
  | { type: "ANOTHER_ORDER_IN_PROGRESS" }
  | { type: "CHECK_ADDRESS_FOR_INSTALLATION_NOT_VERIFIED" }
  | { type: "CHECK_ADDRESS_FOR_INSTALLATION_VERIFIED" }
  | { type: "ADDRESS_VERIFIED"; isAddressInFtthRange: boolean }
  | { type: "ADDRESS_NOT_VERIFIED"; isAddressInFtthRange: boolean }
  | {
      type: "FETCH_CLIENT_ADDRESS_SUCCESS";
      address: IClientAddress;
    }
  | {
      type: "FETCH_CLIENT_ADDRESS_SUCCESS_EMPTY";
    };

export const statusCustomerMachine = createMachine<IContext, IEvent>(
  {
    schema: {
      context: {} as IContext,
      events: {} as IEvent,
    },
    initial: "loading",
    states: {
      loading: {
        invoke: {
          src: "fetchData",
        },
        on: {
          SET_DATA: {
            actions: "setData",
            target: "fetchClientAddress",
          },
          ERROR: {
            target: "error",
          },
        },
      },
      fetchClientAddress: {
        invoke: {
          src: "fetchClientAddress",
        },
        on: {
          FETCH_CLIENT_ADDRESS_SUCCESS: [
            {
              actions: "setClientAddress",
              target: "validateAddress",
            },
          ],
          FETCH_CLIENT_ADDRESS_SUCCESS_EMPTY: [
            {
              target: "ready",
            },
          ],
          ERROR: {
            target: "error",
          },
        },
      },
      validateAddress: {
        invoke: {
          src: "validateAddress",
        },
        on: {
          ADDRESS_VERIFIED: {
            actions: "setFtthRange",
            target: "ready",
          },
          ADDRESS_NOT_VERIFIED: {
            actions: "setFtthRange",
            target: "ready",
          },
        },
      },
      ready: {
        on: {
          NEW_MULTI_ORDER: {
            target: "newMultiOrder",
          },
          NEW_FTTH_ORDER: [
            {
              cond: "isAddressInFtthRange",
              target: "checkAddressForInstallation",
            },
            {
              target: "newFtthOrder",
            },
          ],
          UPDATE_ADDRESS: {
            target: "fetchClientAddress",
          },
        },
      },
      checkAddressForInstallation: {
        invoke: {
          src: "checkAddressForInstallation",
        },
        on: {
          CHECK_ADDRESS_FOR_INSTALLATION_VERIFIED: {
            target: "newFtthOrder",
          },
          CHECK_ADDRESS_FOR_INSTALLATION_NOT_VERIFIED: {
            target: "newFtthOrder",
          },
        },
      },
      newMultiOrder: {
        invoke: {
          src: "newMultiOrder",
        },
        on: {
          NEW_MULTI_ORDER_SUCCESS: {
            actions: "onNewMultiOrderSuccess",
          },
          ANOTHER_AGENT_IN_PROGRESS: {
            target: "onAnotherAgentInProgress",
          },
          ANOTHER_ORDER_IN_PROGRESS: {
            target: "onAnotherOrderInProgress",
          },
          ERROR: {
            target: "error",
          },
        },
      },
      newFtthOrder: {
        invoke: {
          src: "newFtthOrder",
        },
        on: {
          PAYMENT_ERROR: {
            target: "paymentError",
          },
          NEW_FTTH_ORDER_SUCCESS: {
            actions: "onNewFtthOrderSuccess",
          },
          ANOTHER_AGENT_IN_PROGRESS: {
            target: "onAnotherAgentInProgress",
          },
          ANOTHER_ORDER_IN_PROGRESS: {
            target: "onAnotherOrderInProgress",
          },
          ERROR: {
            target: "error",
          },
        },
      },
      error: {
        type: "final",
      },
      paymentError: {
        type: "final",
      },
      onAnotherAgentInProgress: {
        type: "final",
      },
      onAnotherOrderInProgress: {
        type: "final",
      },
    },
  },
  {
    guards: {
      isAddressInFtthRange: ({ isAddressInFtthRange }) => {
        return !!isAddressInFtthRange;
      },
    },
    actions: {
      setData: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET_DATA") {
          return {
            groups: event.groups,
            ftthOrchestrator: event.ftthOrchestrator,
            mobileOrchestrator: event.mobileOrchestrator,
            multiOrderOrchestrator: event.multiOrderOrchestrator,
            modemReturn: event.modemReturn,
            ftthProducts: event.ftthProducts,
            mobileProducts: event.mobileProducts,
          };
        }

        return context;
      }),
      setClientAddress: assign<IContext, IEvent>((context, event) => {
        if (event.type === "FETCH_CLIENT_ADDRESS_SUCCESS") {
          return {
            address: event.address,
          };
        }
        return context;
      }),
      setFtthRange: assign<IContext, IEvent>((context, event) => {
        if (
          event.type === "ADDRESS_VERIFIED" ||
          event.type === "ADDRESS_NOT_VERIFIED"
        ) {
          return {
            isAddressInFtthRange: event.isAddressInFtthRange,
          };
        }
        return context;
      }),
    },
    services: {
      fetchData: () => {
        return async (callback) => {
          try {
            const orchestrators = (await fetchOrchestrators()).unwrap();
            const products = (await fetchOrderProducts()).unwrap();
            const groupsResult = (await fetchGroups()).unwrap();

            return callback({
              type: "SET_DATA",
              groups: groupsResult,
              ftthOrchestrator: orchestrators?.ftth,
              mobileOrchestrator: orchestrators?.mobile,
              multiOrderOrchestrator: orchestrators?.multiOrder,
              modemReturn: orchestrators?.modemReturn,
              ftthProducts: products?.ftthProducts,
              mobileProducts: products?.mobileProducts,
            });
          } catch {
            callback({ type: "ERROR" });
          }
        };
      },
      fetchClientAddress: () => {
        return async (callback) => {
          const result = await fetchClientAddress();

          if (result.isError) {
            return callback({ type: "ERROR" });
          }

          if (result.unwrap().length > 0) {
            return callback({
              type: "FETCH_CLIENT_ADDRESS_SUCCESS",
              address: result.unwrap(),
            });
          }

          return callback({
            type: "FETCH_CLIENT_ADDRESS_SUCCESS_EMPTY",
          });
        };
      },
      validateAddress: ({ address }) => {
        return async (callback) => {
          if (
            !address?.[0]?.geographicalAddress?.municipality ||
            !address?.[0]?.geographicalAddress?.streetNumber ||
            !address[0]?.CBSReference?.municipalityId
          ) {
            return;
          }
          const result = await verifyAddress({
            cityName: address[0]?.geographicalAddress?.municipality,
            streetName: address[0]?.geographicalAddress?.street,
            houseNumber: address[0]?.geographicalAddress?.streetNumber,
            apartmentNumber: address[0]?.geographicalAddress?.apartmentNumber,
            cityId: address[0]?.CBSReference?.municipalityId,
            streetId: address[0]?.CBSReference?.streetId,
          });

          if (result.isError) {
            return callback({
              type: "ADDRESS_NOT_VERIFIED",
              isAddressInFtthRange: false,
            });
          }

          callback({
            type: "ADDRESS_VERIFIED",
            isAddressInFtthRange: true,
          });
        };
      },
      newFtthOrder: () => {
        return async (callback) => {
          try {
            const result = await createOrchestrator("NJU_FTTH_ONBOARDING", {});

            if (result.isError) {
              if (result.err.code === "CLIENT_HAS_UNPAID_INVOICES") {
                return callback({ type: "PAYMENT_ERROR" });
              }
              if (result.err.code === "CLIENT_LOCKED_BY_OTHER_AGENT") {
                return callback({ type: "ANOTHER_AGENT_IN_PROGRESS" });
              }
            }

            const ftth = result.unwrap();

            if (
              ftth &&
              ftth.state !== "START" &&
              ftth.state !== "IDENTITY" &&
              ftth.state !== "PAYMENT_METHOD" &&
              ftth.state !== "IDENTITY_PENDING" &&
              ftth.state !== "APPOINTMENT" &&
              ftth.state !== "ORDER_SUMMARY"
            ) {
              return callback({ type: "ANOTHER_ORDER_IN_PROGRESS" });
            }

            return callback({
              type: "NEW_FTTH_ORDER_SUCCESS",
            });
          } catch {
            return callback({ type: "ERROR" });
          }
        };
      },
      newMultiOrder: () => {
        return async (callback) => {
          try {
            const result = await createOrchestrator(
              "NJU_MULTIORDER_ONBOARDING",
              {},
            );

            if (result.isError) {
              if (result.err.code === "CLIENT_LOCKED_BY_OTHER_AGENT") {
                return callback({ type: "ANOTHER_AGENT_IN_PROGRESS" });
              }

              return callback({ type: "ERROR" });
            }

            const multiOrder = result.unwrap();

            if (
              multiOrder.state !== "START" &&
              multiOrder?.orderStatus !== "Draft"
            ) {
              return callback({ type: "ANOTHER_ORDER_IN_PROGRESS" });
            }

            return callback({
              type: "NEW_MULTI_ORDER_SUCCESS",
            });
          } catch {
            return callback({ type: "ERROR" });
          }
        };
      },
      checkAddressForInstallation: ({ address }) => {
        return async (callback) => {
          console.log(true);
          if (
            !address?.[0]?.geographicalAddress?.municipality ||
            !address?.[0]?.geographicalAddress?.streetNumber ||
            !address[0]?.CBSReference?.municipalityId
          ) {
            return;
          }
          const result = await verifyAddress({
            cityName: address[0]?.geographicalAddress?.municipality,
            streetName: address[0]?.geographicalAddress?.street,
            houseNumber: address[0]?.geographicalAddress?.streetNumber,
            apartmentNumber: address[0]?.geographicalAddress?.apartmentNumber,
            cityId: address[0]?.CBSReference?.municipalityId,
            streetId: address[0]?.CBSReference?.streetId,
          });

          if (result.isError) {
            return callback({
              type: "CHECK_ADDRESS_FOR_INSTALLATION_NOT_VERIFIED",
            });
          }

          window.sessionStorage.setItem(
            "installationAddress",
            JSON.stringify({
              cityId: address[0]?.CBSReference?.municipalityId,
              cityName: address[0]?.geographicalAddress?.municipality,
              houseNumber: address[0]?.geographicalAddress?.streetNumber,
              apartmentNumber: address[0]?.geographicalAddress?.apartmentNumber,
              streetId: address[0]?.CBSReference?.streetId,
              streetName: address[0]?.geographicalAddress?.street,
            }),
          );
          window.sessionStorage.setItem(
            "installationToken",
            result.unwrap().installationToken,
          );

          callback({
            type: "CHECK_ADDRESS_FOR_INSTALLATION_VERIFIED",
          });
        };
      },
    },
  },
);
